import BaseAPIService from '../base/base-api-service';
import PartReturnModel from 'src/model/partReturn.model';
import {
  IEligibleFilter,
  IEligibleResponse,
  IStatusFilter,
  IStatusResponse,
  IEligibleItem,
  IReturnGenerateShippingLabel,
  IReturnShippingLabelDetail,
  IReturnOptionConfig,
  IReturnShippingConfig
} from './typings';

class ReturnsService extends BaseAPIService<PartReturnModel> {
  public returnsEndpoint = 'accountapp/v1/returns';
  public RMAwarehouse = 'navapp/v1/warehouse/warehousesForRma';

 
  constructor() {
    super(PartReturnModel);
  }

  public getEligibleItems(
    body?: IEligibleFilter,
    queryParams?: string
  ): Promise<IEligibleResponse> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/eligible${
        queryParams ? `?${queryParams}` : ''
      }`,
      method: 'post',
      data: body
    }).then(res => res.data);
  }

  public getEligibleCoreItems(
    body?: IEligibleFilter,
    queryParams?: string
  ): Promise<IEligibleResponse> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/eligible/core${
        queryParams ? `?${queryParams}` : ''
      }`,
      method: 'post',
      data: body
    }).then(res => res.data);
  }

  public getStatusItems(
    body?: IStatusFilter,
    queryParams?: string
  ): Promise<IStatusResponse> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists${
        queryParams ? `?${queryParams}` : ''
      }`,
      method: 'post',
      data: body
    }).then(res => res.data);
  }

  public addItemsToRmaList(
    rmaNumber: string,
    list: IEligibleItem[]
  ): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}`,
      method: 'post',
      data: { list }
    }).then(res => res.data);
  }

  public deleteItemFromRmaList(
    rmaNumber: string,
    itemId: number
  ): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}/${itemId}`,
      method: 'delete'
    }).then(response => response.data);
  }

  public updateItemFromRmaList(
    rmaNumber: string,
    part: { id: number; reason: string; returnQuantity: number }
  ): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}/${part.id}`,
      method: 'put',
      data: part
    }).then(response => response.data);
  }

  public createNewRmaList(poNumber?: string): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/createnew`,
      method: 'post',
      data: {
        poNumber
      }
    }).then(res => res.data);
  }

  public deleteRMAList(rmaNumber: string): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}`,
      method: 'delete'
    }).then(res => res.data);
  }

  public getWherehouseList(): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.RMAwarehouse}`
    }).then(res => res.data);
  }

  public submitRMAList(
    body?: IReturnShippingConfig,
    rmaNumber?: string
  ): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}/submit`,
      method: 'post',
      data: body
    }).then(res => res.data);
  }

  public submitRMAListWithShippingLabel(
    body?: IReturnShippingConfig,
    rmaNumber?: string,
    warehouseCode?: string,
    trackingNumbers?:[],
    shippingType?: string
  ): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}/submit`,
      method: 'post',
      data: {
        warehouseCode,
        trackingNumbers,
        shippingType
      }
    }).then(res => res.data);
  }

  public generatePDF(rmaNumber: string, isPrepaidLabel: string): Promise<any> {
    const currentUrl = window.location.pathname.split('/');
    const languageFromUrl = currentUrl[2];
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}/generatepdf?isprepaid=${isPrepaidLabel}&lang=${languageFromUrl}`,
      responseType: 'blob'
    }).then(res => res.data);
  }

  public getCodebarRMAList(rmaNumber: string): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}/codebar`
    }).then(response => response.data);
  }

  public updateRmaList(rmaNumber: string, poNumber: string): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/${rmaNumber}`,
      method: 'put',
      data: {
        poNumber
      }
    }).then(res => res.data);
  }

  public getEligibleItemsExport(
    body?: IEligibleFilter,
    queryParams?: string
  ): Promise<IEligibleResponse> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/eligible/export${
        queryParams ? `?${queryParams}` : ''
      }`,
      responseType: 'blob',
      method: 'post',
      data: body
    }).then(res => res.data);
  }

  public getEligibleItemsExportCore(
    body?: IEligibleFilter,
    queryParams?: string
  ): Promise<IEligibleResponse> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/eligible/core/export${
        queryParams ? `?${queryParams}` : ''
      }`,
      responseType: 'blob',
      method: 'post',
      data: body
    }).then(res => res.data);
  }

  public getStatusItemsExport(
    body?: IStatusFilter,
    queryParams?: string
  ): Promise<IStatusResponse> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/rmalists/export${
        queryParams ? `?${queryParams}` : ''
      }`,
      responseType: 'blob',
      method: 'post',
      data: body
    }).then(res => res.data);
  }

  

  public generateShippingLabel(items:any
  ): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/shippingLabel`,
      method: 'post',
      data: { items }
    }).then(res => res.data);
  }

  public getUpsSyncedLocations(postalCode: string): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.returnsEndpoint}/ups-warehouses?userCurrentZipCode=${postalCode}`
    }).then(response => response.data);
  }

}

const returnsServiceInstance = new ReturnsService();
Object.freeze(returnsServiceInstance);

export default returnsServiceInstance;
